import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

export interface ExtTopPerformanceOpportunityItem {
    userId: number;
    userName: string;
    superGroupRank: string;
    maxRank: number;
    pillarInitials: string;
    pillarName: string;
    pillarImage: string;
    resultImageType: string;
    resultImageText: string;
}

export interface TopPerformanceOpportunityPillar {
    initials: string;
    name: string;
}

export enum TopPerformanceOpportunityPillarResult {
    NULL,
    POSITIVE,
    NEGATIVE,
}

export interface TopPerformanceOpportunityItem {
    username: string;
    superGroupRank: string;
    pillarResults: TopPerformanceOpportunityPillarResult[];
}

export interface TopPerformanceOpportunity {
    pillars: TopPerformanceOpportunityPillar[];
    items: TopPerformanceOpportunityItem[];
}

class TopPerformanceOpportunityItemModelTransformer
    implements
        ModelTransformer<
            TopPerformanceOpportunityItem,
            ExtTopPerformanceOpportunityItem
        >
{
    transform(
        model: ExtTopPerformanceOpportunityItem,
    ): TopPerformanceOpportunityItem {
        return {
            username: model.userName,
            superGroupRank: `${model.superGroupRank}/${model.maxRank}`,
            pillarResults: [],
        };
    }
}

class TopPerformanceOpportunityItemModelSetTransformer extends ModelSetTransformer<
    TopPerformanceOpportunityItem,
    ExtTopPerformanceOpportunityItem
> {
    constructor(private readonly _numPillars: number) {
        super();
    }

    protected newTransformer(): ModelTransformer<
        TopPerformanceOpportunityItem,
        ExtTopPerformanceOpportunityItem
    > {
        return new TopPerformanceOpportunityItemModelTransformer();
    }

    transform(
        model: ExtTopPerformanceOpportunityItem[],
    ): TopPerformanceOpportunityItem[] {
        const transformer = this.newTransformer();
        const transformed: TopPerformanceOpportunityItem[] = [];
        let transformedItem;
        model.forEach((item, index) => {
            const pillarIndex = index % this._numPillars;
            if (pillarIndex === 0) {
                transformedItem = transformer.transform(item);
            }
            transformedItem.pillarResults.push(
                item.resultImageType.toLowerCase() === 'positive'
                    ? TopPerformanceOpportunityPillarResult.POSITIVE
                    : item.resultImageType.toLowerCase() === 'negative'
                    ? TopPerformanceOpportunityPillarResult.NEGATIVE
                    : TopPerformanceOpportunityPillarResult.NULL,
            );
            if (pillarIndex === this._numPillars - 1) {
                transformed.push(transformedItem);
            }
        });
        return transformed;
    }
}

export class TopPerformanceOpportunityModelTransformer
    implements
        ModelTransformer<
            TopPerformanceOpportunity,
            ExtTopPerformanceOpportunityItem[]
        >
{
    transform(
        model: ExtTopPerformanceOpportunityItem[],
    ): TopPerformanceOpportunity {
        const pillars = [];
        model.forEach((value) => {
            if (
                pillars.find((pillar) => pillar.name === value.pillarName) !==
                undefined
            )
                return;
            pillars.push({
                initials: value.pillarInitials,
                name: value.pillarName,
            });
        });

        const items = new TopPerformanceOpportunityItemModelSetTransformer(
            pillars.length,
        ).transform(model);

        return {
            pillars,
            items,
        };
    }
}

export function validateTopPerformanceOpportunity(
    model: TopPerformanceOpportunity,
): void {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const valid =
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        model != null &&
        model.pillars !== null &&
        model.pillars.length !== 0 &&
        model.items != null &&
        model.items.length !== 0;
    if (!valid) {
        throw new Error('TopPerformanceOpportunity not valid');
    }
}
